import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type HeaderAvatarActionId = number;

export type HeaderAvatarActionContextValue = {
  activeActionId: HeaderAvatarActionId | null;
  openAction: (id: HeaderAvatarActionId) => void;
  closeAction: () => void;
  loginMethod?: string | null;
  isdCode?: string | null;
  /** Genius deeplink session — hide Switch Profile / Switch Product until logout. */
  fromGeniusDeeplink?: boolean | null;
};

const HeaderAvatarActionContext =
  createContext<HeaderAvatarActionContextValue | null>(null);

export function ProfileContextProvider({
  children,
  loginMethod,
  isdCode,
  fromGeniusDeeplink,
}: {
  children: ReactNode;
  loginMethod?: string | null;
  isdCode?: string | null;
  fromGeniusDeeplink?: boolean | null;
}) {
  const [activeActionId, setActiveActionId] =
    useState<HeaderAvatarActionId | null>(null);

  const openAction = useCallback((id: HeaderAvatarActionId) => {
    setActiveActionId(id);
  }, []);

  const closeAction = useCallback(() => {
    setActiveActionId(null);
  }, []);

  const value = useMemo(
    () => ({
      activeActionId,
      openAction,
      closeAction,
      loginMethod,
      isdCode,
      fromGeniusDeeplink,
    }),
    [
      activeActionId,
      openAction,
      closeAction,
      loginMethod,
      isdCode,
      fromGeniusDeeplink,
    ],
  );

  return (
    <HeaderAvatarActionContext.Provider value={value}>
      {children}
    </HeaderAvatarActionContext.Provider>
  );
}

export function useHeaderAvatarActions(): HeaderAvatarActionContextValue {
  const context = useContext(HeaderAvatarActionContext);
  if (!context) {
    return {
      activeActionId: null,
      openAction: () => {},
      closeAction: () => {},
      loginMethod: null,
      isdCode: null,
      fromGeniusDeeplink: false,
    };
  }
  return context;
}
